import React, { useEffect, useMemo, useRef } from 'react'
import { rankWith, schemaMatches, uiTypeIs } from '@jsonforms/core'
import {
    useJsonForms,
    withJsonFormsControlProps,
    withTranslateProps,
} from '@jsonforms/react'
import { Unwrapped } from '@jsonforms/material-renderers'
import { Box } from '@mui/material'
import {
    useConfigProblem,
    useDynamicConfigs,
    usePreviewMode,
} from '../JsonFormsWrapper'
import { get } from 'lodash-es'
import { SCHEMA_SHAPE_RANK, UI_TYPE_RANK } from './ranks'

// Tells Jsonforms which control/component should be used. Named by uiType
// it ranks like any other uiType; matched by schema it ranks like any
// other schema match.
const byUiType = rankWith(UI_TYPE_RANK, uiTypeIs('DependentSelectControl'))
const bySchema = rankWith(
    SCHEMA_SHAPE_RANK,
    schemaMatches(
        (schema) =>
            schema.hasOwnProperty('optionsMapPath') &&
            schema.hasOwnProperty('parentField')
    )
)
export const DependentSelectControlTester = (uischema, schema, context) =>
    Math.max(
        byUiType(uischema, schema, context),
        bySchema(uischema, schema, context)
    )

export const DependentSelectControl = (props) => {
    const { path, schema, uischema, data, handleChange, enabled } = props
    const dynamicConfigs = useDynamicConfigs()
    const previewMode = usePreviewMode()
    const onConfigProblem = useConfigProblem()
    const formContext = useJsonForms()
    const parentScope = uischema?.options?.parentScope || schema.parentField
    const dependentMappingScope =
        uischema?.options?.dependentMappingScope || schema.optionsMapPath
    // Get the parent field name from parentScope
    const parentField = parentScope?.split('/').at(-1) ?? ''

    // Either scope missing means the field cannot work at all; the panel
    // below says which, in place of the control.
    let selectError = ''
    if (!parentScope) {
        selectError +=
            'parentField is missing from schema and uischema options\n'
    }
    if (!dependentMappingScope) {
        selectError +=
            'optionsMapPath is missing from schema and uischema options\n'
    }

    // Check if this is being used as a cell renderer (in an array)
    const isCell = path && path.includes('.')

    const { MaterialEnumControl } = Unwrapped

    // Helper function to get the parent path when in an array
    const getParentValueFromPath = (currentPath) => {
        if (isCell) {
            // Extract the array index part
            const pathParts = currentPath.split('.')
            const arrayIndex = pathParts.slice(0, -1).join('.') // Get everything except the last part

            // Construct the full path to the parent value
            const parentPath = `${arrayIndex}.${parentField}`

            const foundValue = get(formContext.core.data, parentPath, false)
            if (foundValue) {
                return foundValue
            }
        }

        // if we are not in an array, or if the parent path was not found in the array,
        // we look for the parent value in the top level of the data object
        return formContext.core.data?.[parentField]
    }

    const parentValue = getParentValueFromPath(path)
    const staticConfigs = formContext.core.schema.properties

    const isMetadata = Boolean(dependentMappingScope?.startsWith('#/metadata/'))
    const isDynamic = Boolean(
        dependentMappingScope?.startsWith('#/definitions/')
    )
    const isMetadataUnsynced =
        isMetadata && schema['x-pandium-metadata-unsynced']

    // The map from parent value to option list. Undefined until the source
    // that carries it — a run's dynamic configs, or synced metadata — exists.
    const optionsMap = useMemo(() => {
        if (!dependentMappingScope) {
            return undefined
        }
        if (isMetadata) {
            return schema['x-pandium-options-map']
        }
        const pathArray = dependentMappingScope
            .replace(isDynamic ? '#/definitions/' : '#/properties/', '')
            .split('/')
        return pathArray.reduce(
            (obj, key) => obj?.[key],
            isDynamic ? dynamicConfigs : staticConfigs
        )
    }, [
        isMetadata,
        isDynamic,
        dependentMappingScope,
        dynamicConfigs,
        staticConfigs,
        schema,
    ])

    const sourceLoaded = Boolean(optionsMap)
    const parentChosen =
        parentValue !== undefined && parentValue !== null && parentValue !== ''

    // Dynamic configs arrive with a tenant's first run; until any exist, a
    // missing map is not a misconfiguration, and the dropdown already says
    // the options have not loaded. Unsynced metadata is not reported either:
    // the disabled dropdown says so.
    const nothingLoadedYet =
        isDynamic && Object.keys(dynamicConfigs ?? {}).length === 0

    // The report is about the field's state, not the handler's identity, so
    // a host that passes a new function each render does not repeat it.
    const reportProblem = useRef(onConfigProblem)
    reportProblem.current = onConfigProblem
    useEffect(() => {
        if (
            !sourceLoaded &&
            !nothingLoadedYet &&
            !selectError &&
            !previewMode &&
            !isMetadataUnsynced
        ) {
            reportProblem.current?.(
                'Something is wrong with the configuration of your integration. ' +
                    'You might need to refresh the configs, or contact an administrator.'
            )
        }
    }, [
        sourceLoaded,
        nothingLoadedYet,
        selectError,
        previewMode,
        isMetadataUnsynced,
    ])

    // `options` is what the dropdown offers, as { label, value } pairs.
    // `message` explains an empty or provisional list; undefined when the
    // list is real.
    const { options, message } = useMemo(() => {
        // Get a user-friendly label for the parent field
        const getParentFieldLabel = () => {
            // Try to find the parent field's title in the schema. The scope
            // is slash-delimited, so it is split into path segments for get.
            const parentFieldSchema = parentScope
                ? get(
                      formContext.core.schema,
                      parentScope.replace('#/', '').split('/')
                  )
                : undefined
            if (parentFieldSchema?.title) {
                return parentFieldSchema.title
            }

            // If in an array, check the parent object's properties
            if (path.includes('.')) {
                // Extract the array path from the current path
                const currentPathParts = path.split('.')
                const arrayPath = currentPathParts[0] // e.g., 'pokemon_list'
                const parentSchema = get(
                    formContext.core.schema,
                    `properties.${arrayPath}.items.properties.${parentField}`
                )
                if (parentSchema?.title) {
                    return parentSchema.title
                }
            }

            // Fall back to the field name, converting snake_case to Title Case
            return parentField
                .split('_')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
        }

        const parentLabel = getParentFieldLabel()

        // Without the source there is no way to tell whether the stored
        // value is still valid, so keep it and show it as the only choice.
        if (!sourceLoaded) {
            const hasData = data !== undefined && data !== null && data !== ''
            return {
                options: hasData ? [{ label: String(data), value: data }] : [],
                message: parentChosen
                    ? 'Options have not loaded yet'
                    : `Please select a ${parentLabel}`,
            }
        }

        if (!parentChosen) {
            return { options: [], message: `Please select a ${parentLabel}` }
        }

        const mapped = optionsMap[parentValue]
        if (!Array.isArray(mapped) || mapped.length < 1) {
            return {
                options: [],
                message: `No options for the selected ${parentLabel}`,
            }
        }

        // Entries are either oneOf-style { title, const } objects or bare
        // values.
        return {
            options: mapped.map((entry) =>
                entry !== null && typeof entry === 'object'
                    ? { label: entry.title, value: entry.const }
                    : { label: String(entry), value: entry }
            ),
        }
    }, [
        optionsMap,
        sourceLoaded,
        parentChosen,
        parentValue,
        parentField,
        parentScope,
        path,
        data,
        formContext.core.schema,
    ])

    // Drop a stored value the loaded options do not contain. This runs after
    // every render, not just when its inputs change, because JSON Forms'
    // own initialisation can overwrite a clear issued during mount; the
    // guard keeps it from firing once the value is already empty. A disabled
    // control never writes.
    useEffect(() => {
        if (
            enabled &&
            sourceLoaded &&
            data !== '' &&
            !options.some((o) => o.value === data)
        ) {
            handleChange(path, '')
        }
    })

    return selectError ? (
        <Box>
            <Box>{uischema.label}</Box>
            <Box sx={{ marginLeft: '20px', whiteSpace: 'pre-line' }}>
                {selectError}
            </Box>
        </Box>
    ) : (
        <MaterialEnumControl
            {...props}
            enabled={
                schema['x-pandium-metadata-unsynced'] ? false : props.enabled
            }
            description={message ?? props.description}
            uischema={
                message
                    ? {
                          ...uischema,
                          options: {
                              ...uischema.options,
                              showUnfocusedDescription: true,
                          },
                      }
                    : uischema
            }
            options={options}
        />
    )
}

export default withJsonFormsControlProps(
    withTranslateProps(DependentSelectControl)
)
